import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ClockIcon,
    UsersIcon,
    CheckCircleIcon,
    BuildingOfficeIcon,
    ChevronRightIcon,
    EllipsisHorizontalIcon,
    UserPlusIcon,
    UserMinusIcon,
} from '@heroicons/react/24/outline';
import {
    CheckCircleIcon as CheckCircleIconSolid,
    ClockIcon as ClockIconSolid,
    XCircleIcon as XCircleIconSolid,
} from '@heroicons/react/24/solid';
import { DashboardClient, MyTasksDashboardDto, EmployeeTaskGroupDto, TaskItemDto } from '../../web-api-client';
import { API_CONFIG } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateHelpers';
import { ERROR_MESSAGES } from '../../constants/messages';

type TabType = 'waitingForMe' | 'waitingForOthers' | 'completed';
type RequestTypeFilter = 'onboarding' | 'offboarding';

function getStatusIcon(status: string) {
    switch (status.toLowerCase()) {
        case 'completed':
            return <CheckCircleIconSolid className="h-4 w-4 text-green-500" />;
        case 'rejected':
            return <XCircleIconSolid className="h-4 w-4 text-red-500" />;
        case 'returned':
            return <CheckCircleIconSolid className="h-4 w-4 text-teal-500" />;
        default:
            return <ClockIconSolid className="h-4 w-4 text-amber-500" />;
    }
}

function getRequestTypeBadge(type: string) {
    const isOnboarding = type.toLowerCase() === 'onboarding';
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            isOnboarding
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
        }`}>
            {isOnboarding ? <UserPlusIcon className="h-3 w-3" /> : <UserMinusIcon className="h-3 w-3" />}
            {type}
        </span>
    );
}

// Employee Card Component
function EmployeeTaskCard({ group, onClick }: { group: EmployeeTaskGroupDto; onClick: () => void }) {
    const pendingTasks = group.tasks?.filter(t => t.statusValue === 0) || [];
    const completedTasks = group.tasks?.filter(t => t.statusValue !== 0) || [];
    const totalTasks = group.tasks?.length || 0;
    const completedCount = completedTasks.length;

    // Get unique categories
    const categories = [...new Set(group.tasks?.map(t => t.category).filter(Boolean) || [])];

    return (
        <div
            onClick={onClick}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer overflow-hidden group"
        >
            {/* Card Header - Employee Info */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-gray-700 dark:to-gray-750 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {group.employeeName}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{group.employeeNumber}</p>
                    </div>
                    <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        <EllipsisHorizontalIcon className="h-5 w-5 text-gray-400" />
                    </button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                    {getRequestTypeBadge(group.requestType || 'Onboarding')}
                    {group.department && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <BuildingOfficeIcon className="h-3 w-3" />
                            {group.department}
                        </span>
                    )}
                </div>
            </div>

            {/* Card Body - Tasks Summary */}
            <div className="p-4">
                {/* Progress Indicator */}
                <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <span>Progress</span>
                        <span>{completedCount}/{totalTasks} tasks</span>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                            style={{ width: `${totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0}%` }}
                        />
                    </div>
                </div>

                {/* Task Preview */}
                <div className="space-y-2">
                    {group.tasks?.slice(0, 3).map((task) => (
                        <div key={task.id} className="flex items-center gap-2 text-sm">
                            {getStatusIcon(task.status || 'Pending')}
                            <span className="truncate text-gray-700 dark:text-gray-300">{task.itemName}</span>
                        </div>
                    ))}
                    {(group.tasks?.length || 0) > 3 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 pl-6">
                            +{(group.tasks?.length || 0) - 3} more tasks
                        </p>
                    )}
                </div>

                {/* Categories Tags */}
                {categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        {categories.slice(0, 3).map((cat, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded">
                                {cat}
                            </span>
                        ))}
                        {categories.length > 3 && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs rounded">
                                +{categories.length - 3}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Card Footer */}
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-750 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {pendingTasks.length} pending
                </span>
                <ChevronRightIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
            </div>
        </div>
    );
}

export default function Dashboard() {
    const [dashboardData, setDashboardData] = useState<MyTasksDashboardDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabType>('waitingForMe');
    const [requestTypeFilter, setRequestTypeFilter] = useState<RequestTypeFilter>('onboarding');
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const client = new DashboardClient(API_CONFIG.baseUrl);
                // Pass user email and identity ID for proper filtering
                const data = await client.getMyTasks(user?.email || '', user?.id || null, null, null);
                setDashboardData(data);
            } catch (error) {
                console.error(ERROR_MESSAGES.FETCH_FAILED('dashboard data'), error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [user?.email, user?.id]);

    const tabs = [
        {
            id: 'waitingForMe' as TabType,
            label: 'Waiting for Me',
            count: dashboardData?.waitingForMeCount || 0,
            icon: ClockIcon
        },
        {
            id: 'waitingForOthers' as TabType,
            label: 'Waiting for Others',
            count: dashboardData?.waitingForOthersCount || 0,
            icon: UsersIcon
        },
        {
            id: 'completed' as TabType,
            label: 'Completed',
            count: dashboardData?.completedCount || 0,
            icon: CheckCircleIcon
        },
    ];

    // Filter data by request type
    const filterByRequestType = (data: EmployeeTaskGroupDto[]): EmployeeTaskGroupDto[] => {
        return data.filter(item =>
            item.requestType?.toLowerCase() === requestTypeFilter
        );
    };

    const getActiveData = (): EmployeeTaskGroupDto[] => {
        if (!dashboardData) return [];
        let data: EmployeeTaskGroupDto[];
        switch (activeTab) {
            case 'waitingForMe':
                data = dashboardData.waitingForMe || [];
                break;
            case 'waitingForOthers':
                data = dashboardData.waitingForOthers || [];
                break;
            case 'completed':
                data = dashboardData.completed || [];
                break;
            default:
                data = [];
        }
        return filterByRequestType(data);
    };

    // Get filtered counts for each tab (counts employee groups)
    const getFilteredCount = (tabId: TabType): number => {
        if (!dashboardData) return 0;
        let data: EmployeeTaskGroupDto[];
        switch (tabId) {
            case 'waitingForMe':
                data = dashboardData.waitingForMe || [];
                break;
            case 'waitingForOthers':
                data = dashboardData.waitingForOthers || [];
                break;
            case 'completed':
                data = dashboardData.completed || [];
                break;
            default:
                return 0;
        }
        return filterByRequestType(data).length;
    };

    const handleCardClick = (group: EmployeeTaskGroupDto) => {
        // Navigate to the onboarding/offboarding request details
        const requestType = group.requestType?.toLowerCase() === 'offboarding' ? 'offboarding' : 'onboarding';
        navigate(`/${requestType}/${group.requestId}`);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Loading skeleton for tabs */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-1">
                    <div className="flex gap-1">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="flex-1 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
                        ))}
                    </div>
                </div>
                {/* Loading skeleton for cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-64 animate-pulse">
                            <div className="bg-gray-100 dark:bg-gray-700 h-20 rounded-t-xl" />
                            <div className="p-4 space-y-3">
                                <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-3/4" />
                                <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-1/2" />
                                <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded w-2/3" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const activeData = getActiveData();

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">My Tasks</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Manage your onboarding and offboarding tasks
                    </p>
                </div>

                {/* Onboarding/Offboarding Toggle */}
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                    <button
                        onClick={() => setRequestTypeFilter('onboarding')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                            requestTypeFilter === 'onboarding'
                                ? 'bg-white dark:bg-gray-700 text-green-700 dark:text-green-400 shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        <UserPlusIcon className="h-4 w-4" />
                        Onboarding
                    </button>
                    <button
                        onClick={() => setRequestTypeFilter('offboarding')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                            requestTypeFilter === 'offboarding'
                                ? 'bg-white dark:bg-gray-700 text-red-700 dark:text-red-400 shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        <UserMinusIcon className="h-4 w-4" />
                        Offboarding
                    </button>
                </div>
            </div>

            {/* Tab Bar - DottedSign Style */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex">
                    {tabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex-1 flex flex-col items-center justify-center py-4 px-3 relative transition-all ${
                                    isActive
                                        ? 'text-blue-600 dark:text-blue-400'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                            >
                                <span className="text-2xl font-bold">{getFilteredCount(tab.id)}</span>
                                <span className="text-xs mt-1 whitespace-nowrap">{tab.label}</span>
                                {isActive && (
                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Content Area */}
            {activeData.length === 0 ? (
                // Empty state
                <div className="text-center py-16">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                        requestTypeFilter === 'onboarding' ? 'bg-green-50 dark:bg-green-900/30' : 'bg-red-50 dark:bg-red-900/30'
                    }`}>
                        {requestTypeFilter === 'onboarding'
                            ? <UserPlusIcon className="h-8 w-8 text-green-400" />
                            : <UserMinusIcon className="h-8 w-8 text-red-400" />
                        }
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No {requestTypeFilter} tasks {activeTab === 'waitingForMe' ? 'waiting for you' : activeTab === 'waitingForOthers' ? 'waiting for others' : 'completed'}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">
                        {activeTab === 'waitingForMe' && `You're all caught up with ${requestTypeFilter} tasks!`}
                        {activeTab === 'waitingForOthers' && `All ${requestTypeFilter} tasks requiring approval have been processed.`}
                        {activeTab === 'completed' && `Completed ${requestTypeFilter} tasks will appear here.`}
                    </p>
                </div>
            ) : (
                // Cards Grid
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {activeData.map((group) => (
                        <EmployeeTaskCard
                            key={`${group.requestId}-${group.employeeId}`}
                            group={group}
                            onClick={() => handleCardClick(group)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
